{
  "common": {
    "loading": "Loading…",
    "errorTitle": "Something went wrong",
    "errorBody": "The data could not be loaded. Reload the page; if this happens again, send this message to support: {{message}}",
    "saveFailed": "Not saved: {{message}}",
    "saved": "✓ Saved",
    "cancel": "Cancel",
    "backTo": "‹ Back to {{place}}",
    "showMore": "Show more",
    "showLess": "Show less"
  },
  "setup": {
    "title": "The dashboard is not set up yet",
    "body": "This copy of the dashboard has no database settings. Add VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY in Netlify (Site configuration → Environment variables) and deploy again."
  },
  "signIn": {
    "title": "Sign in to Opti-Sea",
    "purpose": "Office dashboard for RISQ inspection preparation. We send a one-time code to your email; no password needed.",
    "email": "Your work email",
    "emailInvalid": "Enter a full email address, e.g. name@company.com.",
    "sendCode": "Send me a code",
    "sendFailed": "The code could not be sent: {{message}}",
    "codeSent": "We sent a code to {{email}}. It can take a minute; check the spam folder too.",
    "code": "Code from the email",
    "digit": "Digit {{n}} of the code",
    "verify": "Sign in",
    "codeFailed": "This code did not work. Check it, or ask for a new code.",
    "sendAgain": "Send a new code",
    "otherEmail": "Use another email"
  },
  "noAccess": {
    "title": "No office access",
    "body": "You are signed in, but your account has no office role (DPA or Admin). Masters and officers use the Opti-Sea app on the phone. If you should have office access, contact your Opti-Sea administrator."
  },
  "nav": {
    "main": "Main menu",
    "menu": "Open or close the menu",
    "company": "Company",
    "fleet": "Fleet",
    "waiting": "Waiting for office",
    "weekly": "Questions of the week",
    "vesselsTitle": "Vessels",
    "contentTitle": "Content",
    "content": "Content editor",
    "reassign": "Mass reassignment",
    "adminTitle": "Admin",
    "companies": "Companies",
    "vessels": "Vessels",
    "officeUsers": "Office users",
    "versions": "RISQ versions and imports",
    "youTitle": "You",
    "companySettings": "Company settings"
  },
  "status": {
    "option": "{{icon}} {{word}}"
  },
  "layers": {
    "source": {
      "base": "From the master file",
      "company": "Company setting",
      "vessel": "Changed for this vessel"
    }
  },
  "inspection": {
    "title": "Inspection planning",
    "notSet": "Not set",
    "notSetBody": "No inspection date yet. Set it here or the Master sets it on the phone; then the crew sees what to do now.",
    "planned": "Inspection: {{date}}",
    "daysLeft_one": "in {{count}} day",
    "daysLeft_other": "in {{count}} days",
    "today": "today",
    "daysAndPhase": "{{days}} · {{phase}}",
    "passed": "The planned date has passed — set the next one",
    "phase": {
      "early": "Before the “6 months before” start",
      "six_months": "Now: “6 months before”",
      "three_months": "Now: “3 months before”",
      "last": "Now: the last voyage",
      "final_24h": "Now: the last 24 hours",
      "passed": "Passed"
    },
    "lastFromVoyage": "Last voyage started {{date}}",
    "lastDefault": "The last voyage counts from {{date}} (7 days before) unless its start is recorded",
    "dateLabel": "Planned inspection date",
    "save": "Save date",
    "voyageLabel": "Last voyage started on",
    "voyageHint": "From this day the crew sees the questions marked “Last voyage”. Only the office records it.",
    "voyageSave": "Save last voyage",
    "voyageClear": "Remove last voyage"
  },
  "progress": {
    "byPosition": "By position",
    "byPhase": "By preparation flag",
    "doneLeft": "{{done}} done, {{left}} left ({{percent}}%)",
    "unassigned": "Not assigned to a position",
    "noQuestions": "No questions",
    "noFlagHint": "Done = Closed or Office action. Questions without a time flag count as “3 months before”."
  },
  "fleet": {
    "title": "Fleet — {{company}}",
    "noVessels": "No vessels yet. Admin adds vessels and enrols them in RISQ.",
    "vessel": "Vessel",
    "imo": "IMO {{imo}}",
    "inspection": "Inspection",
    "progress": "Progress",
    "done": "{{done}} of {{total}} done ({{percent}}%)",
    "statuses": "Statuses",
    "statusCounts": "○ {{notStarted}} not started · ◐ {{inProgress}} in progress · ✓ {{closed}} closed",
    "waiting": "Waiting for office",
    "waitingCount_one": "! {{count}} question",
    "waitingCount_other": "! {{count}} questions",
    "none": "None",
    "lastReceived": "Last data received",
    "nothingYet": "Nothing yet",
    "noPerson": "Positions without a person"
  },
  "vessel": {
    "notFound": "This vessel is not in your company.",
    "questions": "Questions",
    "crew": "Crew & access",
    "progress": "Progress: {{done}} of {{total}} done ({{percent}}%)",
    "week": "Questions of the week from {{date}}",
    "chooseWeek": "Choose questions of the week",
    "weekEmpty": "No questions of the week yet.",
    "weekDone": "{{done}} of {{total}} done"
  },
  "waiting": {
    "title": "Waiting for office",
    "titleCount": "Waiting for office ({{count}})",
    "intro": "Office action: the crew's part is done; the question is waiting for the office. The other views show open questions of all vessels.",
    "view": "Which questions",
    "vessel": "Vessel",
    "allVessels": "All vessels",
    "empty": "Nothing is waiting for the office.",
    "emptyOther": "No questions with this status.",
    "since": "Status since",
    "lastNote": "Last note",
    "all": "All {{count}} waiting for office"
  },
  "questions": {
    "title": "Questions — {{vessel}}",
    "search": "Search",
    "searchHint": "Code (e.g. 4.10) or words",
    "section": "Section",
    "sectionCode": "Section {{code}}",
    "position": "Responsible position",
    "unassigned": "Nobody yet",
    "status": "Status",
    "workTypes": "Type of work",
    "timings": "When to prepare",
    "timingOption": "{{short}} — {{meaning}}",
    "timingDefault": "3 months before (no time flag)",
    "count_one": "{{count}} question",
    "count_other": "{{count}} questions",
    "code": "Code",
    "text": "Question",
    "responsible": "Responsible",
    "marks": "Marks"
  },
  "question": {
    "title": "Question {{code}}",
    "codeText": "{{code}} — {{text}}",
    "notFound": "This question is not on this vessel.",
    "edit": "Edit content of this question",
    "attention": "Changed by two people — check",
    "attentionShort": "Check: two people",
    "recheck": "Check again — the question changed in a new RISQ version",
    "responsible": "Responsible: {{positions}} ({{source}})",
    "advice": "Advice",
    "lastNote": "Last note: {{text}}",
    "checklist": "What to check ({{marked}} of {{total}} marked on board: {{done}} done, {{notApplicable}} N/A)",
    "noPoints": "This question has no check points.",
    "companyPoint": " (company point)",
    "companyNotes": "Company notes",
    "guidance": "Inspector guidance",
    "noGuidance": "No inspector guidance.",
    "statusTitle": "Status and notes",
    "saveStatus": "Save status",
    "noteLabel": "Note for the crew",
    "noteHint": "Saved with your name; the crew sees it on the phone after sync.",
    "addNote": "Add note",
    "photos_one": "Photos ({{count}})",
    "photos_other": "Photos ({{count}})",
    "noPhotos": "No photos yet.",
    "photoMissing": "The file has not reached the server yet.",
    "photoOpen": "Open full size",
    "photoCaption": "{{who}}, {{position}} · {{when}}",
    "history": "History",
    "by": "{{who}} · {{when}}",
    "point": {
      "done": "Done: {{text}}",
      "not_applicable": "N/A — does not apply to this vessel: {{text}}",
      "none": "Not marked yet: {{text}}"
    },
    "pointReason": "Reason: {{reason}}"
  },
  "notes": {
    "ims": "IMS references (the whole company sees them)",
    "remarks": "Remarks for the office (only office users see them)",
    "none": "None."
  },
  "activity": {
    "title": "Activity — {{vessel}}",
    "recent": "Recent activity",
    "all": "All activity",
    "empty": "Nothing yet.",
    "when": "When",
    "who": "Who",
    "question": "Question",
    "what": "What",
    "someone": "Someone",
    "system": "System",
    "office": "{{name}} (office)",
    "person": "{{name}}, {{position}}",
    "status": "Status: {{from}} → {{to}}",
    "note": "Note: {{text}}",
    "photoAdded": "Photo added",
    "photoRemoved": "Photo removed",
    "weekly": "Chosen as a question of the week",
    "superseded": "{{text}} (older change, not applied)",
    "unknown": "Change",
    "accessTitle": "Access and planning",
    "assigned": "Assigned as {{position}}",
    "revoked": "Access ended: {{position}}",
    "inspectionPlanned": "Inspection date: {{from}} → {{to}}",
    "lastVoyage": "Last voyage start: {{from}} → {{to}}",
    "noDate": "none",
    "action": "{{action}}",
    "mark": {
      "done": "Check point Done: {{point}}",
      "not_applicable": "Check point N/A: {{point}}",
      "none": "Check point mark removed: {{point}}"
    },
    "markReason": "{{line}} (reason: {{reason}})",
    "aPoint": "a check point"
  },
  "weekly": {
    "title": "Questions of the week",
    "vessel": "Vessel",
    "position": "Position",
    "week": "Week",
    "thisWeek": "This week (from {{date}})",
    "nextWeek": "Next week (from {{date}})",
    "hint": "Choose 5 to {{max}} open questions for the position. The crew sees them under \"This week\" after sync. The choice belongs to the position, not to a person.",
    "chosen": "Chosen: {{count}} of {{max}}",
    "fewHint": "We recommend at least 5.",
    "save": "Save questions of the week",
    "copy_one": "Add {{count}} unfinished from last week",
    "copy_other": "Add {{count}} unfinished from last week",
    "choose": "Choose",
    "chooseQuestion": "Choose question {{code}}"
  },
  "crew": {
    "title": "Crew & access — {{vessel}}",
    "onBoard": "On board now",
    "nobody": "Nobody",
    "since": "{{name}} since {{date}}",
    "history": "Assignment history",
    "activeSince": "Active since {{date}}",
    "ended": "{{from}} – {{to}}: {{reason}}",
    "noReason": "no reason given",
    "former": "Former crew member",
    "giveTitle": "Give access on board",
    "giveBody": "The person gets an account (if new) and an email with how to install the app and sign in. One person per position: a taken position is handed over only after you confirm.",
    "position": "Position",
    "fullName": "Full name",
    "email": "Email address",
    "emailInvalid": "Enter a full email address",
    "give": "Give access",
    "takenNow": "Now: {{name}}",
    "replaceTitle": "Hand over {{position}}?",
    "replaceBody": "{{name}} loses access to {{vessel}} now. Their work stays with the vessel and {{newName}} continues from it.",
    "replaceBodyPerson": "The person already holds another position on {{vessel}}. That position ends now and {{position}} starts.",
    "reason": "Reason (optional)",
    "reasonHint": "For example: crew change",
    "replace": "Hand over",
    "endAccess": "End access",
    "endTitle": "End the access of {{name}}?",
    "endBody": "{{name}} ({{position}}) can no longer see or change the questions of {{vessel}}. The phone removes the vessel data at its next connection. The work stays with the vessel.",
    "given": "Access given: {{name}} is {{position}} on {{vessel}}.",
    "you": "you",
    "endedNow": "Access of {{name}} ended."
  },
  "profile": {
    "title": "Profile",
    "admin": "Role: Admin (all companies)",
    "dpa": "Role: DPA of {{companies}}",
    "language": "Language: {{language}}",
    "signOut": "Sign out"
  },
  "editor": {
    "title": "Content editor",
    "choose": "Choose a question on the left.",
    "openFirst": "Open the first question",
    "keyHint": "Question key {{key}}: your changes are bound to it and stay with new RISQ versions.",
    "wholeCompany": "Whole company",
    "where": "Where",
    "now": "Now",
    "change": "Change",
    "save": "Save",
    "positionsTitle": "Responsible positions",
    "positionsHint": "The company setting applies to every vessel; a vessel setting (also set by the Master on the phone) wins over it. Every change is kept in the history.",
    "positionsFor": "Positions for {{where}}",
    "returnBase": "Return to the master file",
    "returnCompany": "Return to the company setting",
    "flagsTitle": "Marks: type of work and timing",
    "flagsSource": "Now: {{source}}",
    "noTimingHint": "No time flag: the crew sees the question with the “3 months before” questions.",
    "saveForCompany": "Save for the company",
    "saveBase": "Change the master file content (all companies)",
    "baseHint": "Admin only. The base changes for every company; the next import of the master file shows the difference.",
    "checklistTitle": "What to check",
    "checklistHint": "Change the text of a point, hide it, or add your own points. Your vessels see the company version; other companies never do.",
    "noBasePoints": "This question has no points in the master file.",
    "changedText": "Text changed by the company",
    "hidden": "Hidden",
    "baseText": "Master file text: {{text}}",
    "editText": "Change text",
    "pointText": "Text of the check point",
    "hide": "Hide",
    "show": "Show again",
    "ownPoints": "Your own points",
    "noOwnPoints": "No own points yet.",
    "ownPoint": "Own point",
    "remove": "Remove",
    "newPoint": "New check point",
    "addPoint": "Add check point",
    "notesTitle": "Company notes",
    "notesHint": "Notes from the master file change only through the master file. Notes added here can be changed or removed.",
    "fromMasterFile": "From the master file",
    "addedByOffice": "Added in the dashboard",
    "noteKind": "Kind",
    "noteText": "Text",
    "addNote": "Add note",
    "imsAudience": "IMS references are shown to everyone in the company, also on the phone.",
    "remarkAudience": "Remarks for the office are shown only to office users."
  },
  "history": {
    "title": "History of changes",
    "empty": "No changes yet: everything is as in the master file.",
    "what": "What",
    "before": "Before",
    "after": "After",
    "nothing": "—",
    "from": " ({{source}})",
    "hidden": " (hidden)",
    "actor": "{{name}} ({{role}})",
    "roles": {
      "admin": "Admin",
      "dpa": "Office",
      "master": "Master",
      "service": "System"
    },
    "targets": {
      "positions": "Responsible",
      "wtc_item": "Check point",
      "flags": "Marks"
    },
    "actions": {
      "set": "changed",
      "add": "added",
      "revert": "returned",
      "remove": "removed"
    }
  },
  "mass": {
    "title": "Mass reassignment",
    "step1": "1. Where",
    "where": "Change for",
    "company": "The whole company ({{company}})",
    "vessel": "Only {{vessel}}",
    "step2": "2. Choose questions",
    "shownSelected": "{{shown}} shown · {{selected}} chosen",
    "selectShown": "Choose all shown",
    "unselectShown": "Unchoose all shown",
    "clearSelection": "Clear choice",
    "nowHere": "Responsible now ({{where}})",
    "step3": "3. New responsible position",
    "modeSet": "Give to these positions",
    "modeRevertCompany": "Return to the master file",
    "modeRevertVessel": "Return to the company setting",
    "newPositions": "New position(s)",
    "previewLine": "{{change}} questions will change; {{unchanged}} already as chosen.",
    "review_one": "Review {{count}} change",
    "review_other": "Review {{count}} changes",
    "confirmTitle": "Change {{count}} questions for {{where}}?",
    "confirmBody": "Each change is kept in the question's history and can be returned. Phones get it at the next sync.",
    "questions": "Questions",
    "codes": "Questions: {{codes}}",
    "confirm_one": "Change {{count}} question",
    "confirm_other": "Change {{count}} questions",
    "done_one": "✓ Done: {{count}} question changed for {{where}}.",
    "done_other": "✓ Done: {{count}} questions changed for {{where}}."
  },
  "admin": {
    "onlyAdmin": "Only Admin can open this page.",
    "companiesTitle": "Companies",
    "newCompany": "New company",
    "companyName": "Name",
    "shortName": "Short name",
    "create": "Create",
    "risqModule": "RISQ module",
    "on": "On",
    "off": "Off",
    "vesselsTitle": "Vessels",
    "newVessel": "New vessel",
    "company": "Company",
    "vesselName": "Vessel name",
    "imo": "IMO number",
    "imoInvalid": "7 digits with a correct check digit",
    "vesselType": "Type",
    "flag": "Flag",
    "risqItems": "RISQ",
    "items_one": "{{count}} question",
    "items_other": "{{count}} questions",
    "enroll": "Enrol in RISQ",
    "enrollAgain": "Add new questions",
    "officeUsersTitle": "Office users",
    "person": "Person",
    "role": "Role",
    "access": "Access",
    "dpa": "DPA",
    "since": "Since {{date}}",
    "ended": "Ended {{date}}",
    "contentTitle": "RISQ versions and imports",
    "versions": "Versions",
    "current": "Current",
    "notCurrent": "Stored, not current",
    "imports": "Import log",
    "noImports": "No imports yet.",
    "versionCode": "RISQ {{code}}",
    "inviteTitle": "Give office access (DPA)",
    "inviteBody": "The person gets an account (if new) and an email with the dashboard address. A DPA sees and manages all vessels of the company.",
    "invite": "Give office access",
    "given": "Office access given: {{name}} is DPA of {{company}}.",
    "endAccess": "End access",
    "endTitle": "End the office access of {{name}}?",
    "endBody": "{{name}} can no longer open the dashboard for {{company}}. Everything they did stays in the history.",
    "endedNow": "Office access of {{name}} ended."
  },
  "finalChecks": {
    "title": "Final checks",
    "titleProgress": "Final checks: {{done}} of {{total}} done ({{percent}}%)",
    "noList": "Your company has no Final checks list yet.",
    "open": "Open now: the crew ticks the list for the inspection on {{date}}.",
    "opensOn": "The crew ticks the list from {{date}}, the day before the inspection on {{inspection}}.",
    "group": "Group",
    "tickedBy": "Ticked by",
    "done": "Done",
    "valueEmpty": "{{label}}: not entered",
    "valueSet": "{{label}}: {{value}} ({{position}})"
  },
  "invite": {
    "emailSent": "The invitation email was sent to {{email}}.",
    "emailNotConfigured": "No invitation email was sent (email sending is not set up). Tell the person to install Opti-Sea and sign in with {{email}}.",
    "emailFailed": "The invitation email could not be sent. Tell the person to install Opti-Sea and sign in with {{email}}.",
    "newAccount": "A new account was created.",
    "errors": {
      "invalid_request": "Check the name, the email address and the position.",
      "not_signed_in": "Your sign-in has expired. Sign in again.",
      "not_allowed": "You are not allowed to give this access.",
      "not_found": "The vessel or company was not found.",
      "position_taken": "The position is taken.",
      "server_error": "Something went wrong on the server. Try again.",
      "network": "No connection to the server. Check the internet and try again.",
      "method_not_allowed": "Something went wrong on the server. Try again.",
      "admin_account": "Something went wrong on the server. Try again."
    }
  },
  "companySettings": {
    "title": "Company settings — {{company}}",
    "positionsTitle": "Positions on your vessels",
    "positionsBody": "Untick a position your company does not use (for example Third Engineer). It stays in the system but leaves every list of positions here and on the phones after their next sync. Questions keep their distribution.",
    "alwaysUsed": "Always used: access rules depend on it",
    "inUse": "Somebody holds this position on one of your vessels. End that access first (Crew & access).",
    "failed": "The settings were not saved. Try again.",
    "saved": "Saved. Phones get it with their next sync.",
    "save": "Save"
  }
}
